// @flow
import { type DocType } from '../types.js';
import { components } from '../../../index.js';
type ComponentName = $Keys<typeof components>;

// ----------------------------------------------------------------------------
//  Component sets (parent-child pairings)
// ----------------------------------------------------------------------------
const CartesianPlaneComponents = [
  'CartesianPlane',
  'Point',
  'Line',
  'Ray',
  'LineSegment',
  'Vector',
  'FunctionPlot',
  'FunctionInequality',
  'Polygon',
  'Circle',
  'Ellipse',
  'Hyperbola',
  'Parabola',
];

const NumberLineComponents = ['NumberLine', 'NLPoint', 'NLInterval'];

const StemAndLeafPlotComponents = [
  'StemAndLeafPlot',
  'DataSet',
  'StemAndLeafPlotKey',
];

const MultipleChoiceComponents = ['MultipleChoice', 'Choice'];

const PartsComponents = ['Parts', 'Part'];

const PerPartTasksComponents = ['PerPartTasks', 'PerPartTask'];

const ListComponents = ['List', 'ListItem'];

const ColumnComponents = ['TwoColumn', 'Column'];

const TwoColumnProofComponents = [
  'TwoColumnProof',
  'ProofGoal',
  'ProofRow',
  'ProofStatement',
  'ProofReason',
];

const AlgebraicWorkingComponents = [
  'AlgebraicWorking',
  'AlgebraicWorkingRow',
  'AlgebraicWorkingStatement',
  'AlgebraicWorkingExplanation',

  // NOTE whilst AlgebraicWorkingStepIndicator is not technically a descendent
  // of AlgebraicWorking, we'll include it in the set as we generally want
  // the ability to refer to steps in the AlgebraicWorking component in other
  // parts of the content.
  'AlgebraicWorkingStepIndicator',
];

// These components are shared by both the Solidify Lesson and the
// Solidify Teacher Note documents.
const sharedSolidifyComponents = [
  'SolidifyWorkedExample',
  'SolidifyWorkedExamplePart',
  'SolidifyProblemStatement',
  'SolidifySubproblemStatement',
  'SolidifyApproach',
  'SolidifySolution',
  'SolidifyReflection',
  'SolidifyPartIndicator',
  'SolidifyGlossaryTerm',
  'SolidifyEmphasis',
  'SolidifyAnnotatedFigure',
  'SolidifyFigureAnnotation',
  'SolidifyFigureGrid',
  'SolidifyFigureGridItem',
  'SolidifyFigureGridCaption',
  'SolidifyTechnologyToolScreenshot',
];

const PseudoCodeBlockComponents = ['PseudoCodeBlock', 'LineOfCode'];

// ----------------------------------------------------------------------------
//  Component whitelists by document
// ----------------------------------------------------------------------------

const sharedRetrofittedStudentLessonComponents = [
  'RSLAnnotatedFigure',
  'RSLFigureAnnotation',
  'RSLEmphasis',
  'RSLExplainedEquation',
  'RSLExplainedEquationLatex',
  'RSLExplainedEquationDescription',
  'RSLFigureGrid',
  'RSLFigureGridItem',
  'RSLFigureGridCaption',
  'RSLGlossaryTermsList',
  'RSLTermDefinition',
  'RSLDefinitionDescription',
  'RSLDefinitionExample',
  'RSLDefinitionFigure',
  'RSLGlossaryTerm',
  'RSLMultipleChoiceImageOptions',
  'RSLMultipleChoiceImageOption',
  'RSLMultipleChoiceTextOptions',
  'RSLMultipleChoiceTextOption',
  'RSLPriorKnowledgeLink',
  'RSLTechnologyToolScreenshot',
];

const RetrofittedStudentLesson = [
  // The document type itself
  'RetrofittedStudentLesson',

  // RSL Shared components
  ...sharedRetrofittedStudentLessonComponents,

  // RetrofittedStudentLesson-specific structural components
  'RSLIntroduction',
  'RSLIdeaSection',
  'RSLIdeaTheory',
  'RSLIdeaExploration',
  'RSLInteractiveIdeaExploration',
  'RSLIdeaWorkedQuestions',
  'RSLIdeaSummary',
  'RSLWorkedQuestion',
  'RSLWorkedQuestionPart',
  'RSLQuestionStatement',
  'RSLSubquestionStatement',
  'RSLCreateAStrategy',
  'RSLApplyTheIdea',
  'RSLReflectAndCheck',

  // RetrofittedStudentLesson-specific primitives
  'RSLGeogebra',
  'RSLBunnyVideo',

  // Basic text stuff
  'Text',
  'Latex',
  'LatexTextAtom',
  ...ListComponents,

  // Basic figures
  'Image',
  'DraftImage',
  'TabularData',
  'BoxAndWhiskerPlot',
  ...CartesianPlaneComponents,
  ...PseudoCodeBlockComponents,
  ...NumberLineComponents,
  ...StemAndLeafPlotComponents,
  ...TwoColumnProofComponents,
  ...AlgebraicWorkingComponents,
];

const RetrofittedStudentLessonTeacherNote = [
  // The document type itself
  'RetrofittedStudentLessonTeacherNote',

  // RSL Shared components
  ...sharedRetrofittedStudentLessonComponents,

  // RetrofittedStudentLessonTeacherLesson-specific structural components
  'RSLTNIntroduction',
  'RSLTNPriorKnowledgeSuggestedReview',
  'RSLTNLessonSupports',
  'RSLTNIdeaSection',
  'RSLTNIdeaTheory',

  // RetrofittedStudentLessonTeacherLesson Exploration components
  'RSLTNIdeaExploration',
  'RSLTNExplorationExplanation',
  'RSLTNIdealResponses',
  'RSLTNCompletedFigures',
  'RSLTNCompletedFigure',
  'RSLTNCompletedFigureAnnotation',
  'RSLTNPurposefulQuestions',
  'RSLTNCommonMisconceptions',
  'RSLTNQuestionWithResponses',
  'RSLTNPurposefulQuestion',
  'RSLTNPossibleMisunderstandings',
  'RSLTNMisunderstanding',
  'RSLTNGivenQuestion',
  'RSLTNResponse',

  // RetrofittedStudentLessonTeacherLesson Instructional Support components
  'RSLTNInstructionalSupports',
  'RSLTNInstructionalSupportInformation',
  'RSLTNEnglishLearnerSupport',
  'RSLTNInstructionalSupportDisability',
  'RSLTNCommonMisconceptions',

  // RetrofittedStudentLessonTeacherLesson Example components
  'RSLTNTeacherLedExamples',
  'RSLTNTeacherLedExamplesPreamble',
  'RSLTNTeacherLedExample',
  'RSLTNExampleStatement',
  'RSLTNExampleGuidance',
  'RSLTNTeacherLedExamplePart',
  'RSLTNPurpose',
  'RSLTNSolution',
  'RSLTNExpectedMistakes',
  'RSLTNReflection',
  'RSLTNSubexampleStatement',

  // RetrofittedStudentLessonTeacherLesson Example components
  'RSLTNTools',
  'RSLTNCalculatorTool',
  'RSLTNTool',
  'RSLTNRichTask',
  'RSLTNRichTaskGoogleDoc',

  // Basic text stuff
  'Text',
  'Latex',
  'LatexTextAtom',
  ...ListComponents,

  // Basic figures
  'Image',
  'DraftImage',
  'TabularData',
  'BoxAndWhiskerPlot',
  ...CartesianPlaneComponents,
  ...PseudoCodeBlockComponents,
  ...NumberLineComponents,
  ...StemAndLeafPlotComponents,
  ...TwoColumnProofComponents,
  ...AlgebraicWorkingComponents,
];

const Worksheet = [
  // The document type itself
  'Worksheet',

  // These are worksheet/answerkey specific components.
  'UglyLegacyImage',
  'DraftImage',
  'QuestionsGroup',
  'Question',
  ...PartsComponents,
  ...PerPartTasksComponents,
  ...MultipleChoiceComponents,

  // These are "general" components that are allowed in worksheets
  // Basic text stuff
  'Text',
  'Latex',
  'LatexTextAtom',
  ...ListComponents,
  'Bold',
  'Italics',
  // Basic layout
  ...ColumnComponents,
  // Basic figures
  'Image',
  'TabularData',
  'BoxAndWhiskerPlot',
  ...CartesianPlaneComponents,
  ...PseudoCodeBlockComponents,
  ...NumberLineComponents,
  ...StemAndLeafPlotComponents,
  ...TwoColumnProofComponents,
  ...AlgebraicWorkingComponents,
];

const AnswerKey = [
  // The document type itself
  'AnswerKey',

  // These are worksheet/answerkey specific components.
  'UglyLegacyImage',
  'DraftImage',
  'QuestionsGroup',
  'Question',
  ...PartsComponents,
  // Explicitly not allowing this in AnswerKey as I don't think there is
  // a rationale for it.
  // ...PerPartTasksComponents,
  ...MultipleChoiceComponents,

  // These are "general" components that are allowed in worksheets
  // Basic text stuff
  'Text',
  'Latex',
  'LatexTextAtom',
  ...ListComponents,
  'Bold',
  'Italics',
  // Basic layout
  ...ColumnComponents,
  // Basic figures
  'Image',
  'TabularData',
  'BoxAndWhiskerPlot',
  ...CartesianPlaneComponents,
  ...PseudoCodeBlockComponents,
  ...NumberLineComponents,
  ...StemAndLeafPlotComponents,
  ...TwoColumnProofComponents,
  ...AlgebraicWorkingComponents,
];

const PrimaryTeacherNote = [
  // The document type itself
  'PrimaryTeacherNote',

  'PTNSummary',
  'PTNConnect',
  'PTNConcept',
  'PTNChallengeActivity',
  'PTNConfusingConcepts',
  'PTNBunnyVideo',

  // 🚨 These are worksheet-specific components that should not have been enabled
  // in PrimaryTeacherNotes, but were. We'll try to purge these eventually.
  'QuestionsGroup',
  'Question',

  // These are components that were thought to be general, but for now
  // are actually PTN-only.
  'ExternalLink',
  'GeogebraIframe',

  // These are "general" components that are allowed in PTNs
  // Basic text stuff
  'Text',
  'Latex',
  'LatexTextAtom',
  ...ListComponents,
  'Bold',
  'Italics',
  // Basic layout
  ...ColumnComponents,
  // Basic figures
  'Image',
  'DraftImage', // 🚨 We'd like to get rid of this, but it was in there before.
  'TabularData',
  'BoxAndWhiskerPlot',
  ...CartesianPlaneComponents,
  ...NumberLineComponents,
  ...StemAndLeafPlotComponents,
];

const ActivityTeacherNote = [
  'ActivityTeacherNote',

  'ATNActivityName',
  'ATNOverview',
  'ATNCalloutBox',
  'ATNHeading',
  'ATNDetails',

  // Basic text stuff
  'Text',
  'Bold',
  'Latex',
  'LatexTextAtom',
  ...ListComponents,

  // Basic figures
  'Image',
  // For solutions
  'DraftImage',
  'TabularData',
  'BoxAndWhiskerPlot',
  ...CartesianPlaneComponents,
  ...NumberLineComponents,
  ...StemAndLeafPlotComponents,
];

// The two Solidify documents share several components
// Those that are not shared have a different prefix

const SolidifyLesson = [
  // The document type itself
  'SolidifyLesson',

  ...sharedSolidifyComponents,

  // Solidify Lesson only
  'SLDWorkedExamples',
  'SLDConceptSummary',
  'SLDTermDefinition',
  'SLDDefinitionDescription',
  'SLDDefinitionExample',
  'SLDDefinitionFigure',
  'SLDExplainedEquation',
  'SLDExplainedEquationLatex',
  'SLDExplainedEquationDescription',

  // Basic text stuff
  'Text',
  'Latex',
  'LatexTextAtom',
  ...ListComponents,

  // Basic figures
  'Image',
  'DraftImage',
  'TabularData',
  'BoxAndWhiskerPlot',
  ...CartesianPlaneComponents,
  ...NumberLineComponents,
  ...StemAndLeafPlotComponents,
  ...TwoColumnProofComponents,
  ...AlgebraicWorkingComponents,
];

const SolidifyTeacherNote = [
  // The document type itself
  'SolidifyTeacherNote',

  ...sharedSolidifyComponents,

  // Solidify Teacher Note only
  'STNInstructionalSupport',
  'STNInstructionalSupportDisability',
  'STNInstructionalSupportInformation',
  'STNAdditionalExamples',
  'STNAdditionalExample',
  'STNCommonMisconceptions',
  'STNEnglishLearnerSupport',
  'SolidifyWorkedExample',
  'SolidifyWorkedExamplePart',
  'SolidifyProblemStatement',
  'SolidifySubproblemStatement',
  'SolidifyApproach',
  'SolidifySolution',
  'SolidifyReflection',
  'SLDPartIndicator',

  // Basic text stuff
  'Text',
  'Latex',
  'LatexTextAtom',
  ...ListComponents,

  // Basic figures
  'Image',
  'DraftImage',
  'TabularData',
  'BoxAndWhiskerPlot',
  ...CartesianPlaneComponents,
  ...NumberLineComponents,
  ...StemAndLeafPlotComponents,
  ...TwoColumnProofComponents,
  ...AlgebraicWorkingComponents,
];

const TeacherSubtopicOverview = [
  // The document type itself
  'TeacherSubtopicOverview',

  // Subtopic overview components
  'TSOLessonNarrative',
  'TSOLearningTargetsList',
  'TSOLearningTarget',
  'TSOKeyVocabularyList',
  'TSOKeyVocabularyItem',
  'TSOEssentialUnderstanding',
  'TSOStandardsSection',
  'TSOMathematicalPracticeStandardsList',
  'TSOMathematicalPracticeStandard',
  'TSOMathematicalPracticeStandardDescription',
  'TSOMathematicalPracticeSuggestedApproach',
  'TSOContentStandardsList',
  'TSOPriorConnectionsList',
  'TSOFutureConnectionsList',
  'TSOContentStandard',
  'TSOContentStandardDescription',

  // Florida-specific components
  'TSOStandardsAndBenchmarksSection',
  'TSOFloridaMTRStandardsList',
  'TSOFloridaMTRStandard',
  'TSOFloridaBenchmarksList',
  'TSOFloridaBenchmark',
  'TSOFloridaConnectionsList',
  'TSOFloridaAccessPointsList',
  'TSOFloridaAccessPoint',

  // Virginia-specific components
  'TSOVirginiaMathematicalProcessGoalList',
  'TSOVirginiaMathematicalProcessGoal',
  'TSOVirginiaMathematicalProcessGoalDescription',
  'TSOVirginiaMathematicalProcessGoalSuggestedApproach',

  // Basic text stuff
  'Text',
  'Latex',
  'LatexTextAtom',
];

const EngageActivityNote = [
  // The document type itself
  'EngageActivityNote',

  // EngageActivityNote components
  'EANIntroduction',
  'EANActivityDescription',
  'EANSkillSection',
  'EANPrerequisiteSkills',
  'EANMainSkills',
  'EANExtensionSkills',
  'EANSkill',
  'EANPreparationSection',
  'EANPreparationList',
  'EANPreparationListItem',
  'EANDisabilitySupportPanel',
  'EANEnglishSupportPanel',
  'EANClassroomGuide',
  'EANHookSection',
  'EANHookDescription',
  'EANHookPanel',
  'EANContinueCondition',
  'EANLaunchSection',
  'EANLaunchDescription',
  'EANUnderstandingCheck',
  'EANContextCheck',
  'EANGroupingCheck',
  'EANExploreSection',
  'EANExploreSubsection',
  'EANExploreSubsectionDescription',
  'EANStrategyPanel',
  'EANStrategyDetail',
  'EANMisconceptionsPanel',
  'EANMisconceptionsDetail',
  'EANMisconceptionsItem',
  'EANPitfallItem',
  'EANDiscussSection',
  'EANDiscussDescription',
  'EANDiscussPanel',
  'EANPurposefulQuestionsPanel',
  // Basic text stuff
  'Text',
  'Latex',
  'LatexTextAtom',
  ...ListComponents,
  // Basic figures
  'Image',
  'DraftImage',
  'TabularData',
  'BoxAndWhiskerPlot',
  ...CartesianPlaneComponents,
  ...NumberLineComponents,
  ...StemAndLeafPlotComponents,
  ...TwoColumnProofComponents,
  ...AlgebraicWorkingComponents,
];

const StudentSubtopicOverview = [
  // The document type itself
  'StudentSubtopicOverview',

  // Subtopic overview components
  'SSOLearningTargetsList',
  'SSOLearningTarget',
  'SSOKeyVocabularyList',
  'SSOKeyVocabularyListItem',
  'SSOEssentialQuestionsList',
  'SSOEssentialQuestion',

  // Basic text stuff
  'Text',
  'Latex',
  'LatexTextAtom',
];

const Investigation = [
  'Investigation',
  // Investigation structural components
  'INVOverview',
  'INVOverviewIntroduction',
  'INVActivitySection',
  'INVInformation',
  'INVExpand',
  'INVReflection',
  'INVProcedure',

  // Callout lists
  'INVObjectives',
  'INVObjective',
  'INVMaterials',
  'INVMaterialsItem',
  'INVDownloadableAssets',
  'INVDownloadableAsset',
  'INVProcedureRequirements',
  'INVRequirementItem',

  // Investigation Chunks
  'INVInformationChunks',
  'INVChunk',
  'INVChunkFocus',
  'INVChunkImage',
  'INVChunkLatex',
  'INVChunkText',
  'INVChunkList',
  'INVChunkListItem',

  // Investigation Actions
  'INVProcedurePreamble',
  'INVStep',
  'INVSteps',
  'INVStepText',
  'INVStepList',
  'INVStepListContext',
  'INVStepListItem',
  'INVStepQuestions',
  'INVStepQuestion',
  'INVStepFigure',
  'INVInstructionalPhoto',
  'INVSpreadsheetScreenshot',
  'INVStepTable',
  'INVResourceLink',

  // Investigation questions
  'INVQuestionsPreamble',
  'INVQuestion',
  'INVInvestigateQuestions',
  'INVDiscussionQuestions',

  // Other useful investigation scoped components
  'INVEmphasis',
  'INVInternalLessonLink',
  'INVExplainedEquation',
  'INVExplainedEquationLatex',
  'INVExplainedEquationDescription',
  'INVGeogebra',
  'INVTechnologyToolScreenshot',
  'INVBunnyVideo',

  // Basic text stuff
  'Text',
  'Latex',
  'LatexTextAtom',

  // Basic figures
  'Image',
  'DraftImage',
  'TabularData',
  'BoxAndWhiskerPlot',
  'AlgebraicWorkingStepIndicator',
  ...CartesianPlaneComponents,
  ...NumberLineComponents,
  ...StemAndLeafPlotComponents,
  ...AlgebraicWorkingComponents,
  ...PseudoCodeBlockComponents,
];

const InvestigationTeacherNote = [
  'InvestigationTeacherNote',
  // InvestigationTeacherNote overview components
  'INVTNOverview',
  'INVTNOverviewDescription',
  'INVTNSkillSection',
  'INVTNPreparationSection',
  'INVTNPrerequisiteSkills',
  'INVTNMainSkills',
  'INVTNExtensionSkills',
  'INVTNSkill',
  'INVTNPreparationList',
  'INVTNPreparationListItem',
  'INVTNInstructionalSupports',
  'INVTNInstructionalSupportInformation',
  'INVTNEnglishLearnerSupport',
  'INVTNInstructionalSupportDisability',

  // InvestigationTeacherNote Activity components
  'INVTNActivityGuide',
  'INVTNBackgroundSection',
  'INVTNActionsSection',
  'INVTNActionsSuggestedVariation',
  'INVTNActionsSituation',
  'INVTNActionsVariation',
  'INVTNInvestigateSection',
  'INVTNInvestigateAnswer',
  'INVTNBackgroundDescription',
  'INVTNBackgroundPanel',
  'INVTNActionsDescription',
  'INVTNActionsPanel',
  'INVTNInvestigateDescription',
  'INVTNAnswersPanel',
  'INVTNMisconceptionsPanel',
  'INVTNMisconceptionsDetail',
  'INVTNMisconceptionsItem',
  'INVTNPitfallItem',

  // InvestigationTeacherNote Reflect components
  'INVTNReflect',
  'INVTNDiscussionSection',
  'INVTNDiscussionDescription',
  'INVTNDiscussionPanel',

  // Other common components
  'INVTNContinueCondition',
  'INVDownloadableAssets',
  'INVDownloadableAsset',
  'INVQuestionsPreamble',
  'INVQuestion',

  // Basic text stuff
  'Text',
  'Latex',
  'LatexTextAtom',

  // Basic figures
  'Image',
  'DraftImage',
  'TabularData',
  'BoxAndWhiskerPlot',
  'AlgebraicWorkingStepIndicator',
  ...CartesianPlaneComponents,
  ...NumberLineComponents,
  ...StemAndLeafPlotComponents,
  ...AlgebraicWorkingComponents,
  ...PseudoCodeBlockComponents,
];

const Playground = [
  // The document type itself
  'Playground',
  // Playground is currently our document without restriction so we include
  // all components that are allowed in any of our static documents
  ...[
    ...new Set([
      ...Worksheet,
      ...AnswerKey,
      ...PrimaryTeacherNote,
      ...ActivityTeacherNote,
      ...SolidifyLesson,
      ...SolidifyTeacherNote,
      ...TeacherSubtopicOverview,
      ...StudentSubtopicOverview,
      ...EngageActivityNote,
      ...RetrofittedStudentLesson,
      ...RetrofittedStudentLessonTeacherNote,
      ...Investigation,
      ...InvestigationTeacherNote,
    ]),
  ],
];

const EngageActivity = [
  // The document type itself
  'EngageActivity',

  // Basic text stuff
  'Text',
  'Latex',
  'LatexTextAtom',
  ...ListComponents,

  // Figures
  'TabularData',
  'BoxAndWhiskerPlot',
  // NOTE we have a bespoke EngageCartesianPlane that must be used instead
  // thus we ban the use of <CartesianPlane>
  ...CartesianPlaneComponents.filter((c) => c !== 'CartesianPlane'),
  ...NumberLineComponents,
  ...StemAndLeafPlotComponents,
  ...AlgebraicWorkingComponents,

  // Structural activity components
  'Slides',
  'Slide',
  'InstructionalRoutineSlide',

  // Content activity components
  'GeogebraSubmission',
  'ImageSubmission',
  'Panel',
  'WrittenResponse',
  'StudentAction',
  'StudentActionSupportingText',
  'ActivityPhoto',
  'ActivityGraphic',
  'ActivityDraftImage',
  'DownloadableAsset',
  'ImageOptionsPicker',
  'ImageOption',
  'TextOptionsPicker',
  'TextOption',
  'EngageCartesianPlane',
];

const TopicOverview = [
  'TopicOverview',
  'TOVBigIdeasSection',
  'TOVBigIdea',
  'TOVEssentialUnderstandingList',
  'TOVEUItem',
  'TOVFKItem',
  'TOVFoundationalKnowledgeList',
  'TOVFutureConnectionsList',
  'TOVFCItem',
  'TOVNarrative',
  // Basic text stuff
  'Text',
  'Latex',
  'LatexTextAtom',
  ...ListComponents,

  // Basic figures
  'Image',
  'TabularData',
  'BoxAndWhiskerPlot',
  ...CartesianPlaneComponents,
  ...PseudoCodeBlockComponents,
  ...NumberLineComponents,
  ...StemAndLeafPlotComponents,
  ...TwoColumnProofComponents,
  ...AlgebraicWorkingComponents,
];

const TopicAssessment = [...Worksheet, 'TopicAssessment'].filter(
  (x) => x !== 'Worksheet',
);
const TopicAnswerKey = [...AnswerKey, 'TAKBenchmark', 'TopicAnswerKey'].filter(
  (x) => x !== 'AnswerKey',
);

const componentWhitelistsByDocType = {
  Worksheet,
  AnswerKey,
  PrimaryTeacherNote,
  Playground,
  EngageActivity,
  ActivityTeacherNote,
  SolidifyLesson,
  SolidifyTeacherNote,
  TopicOverview,
  TopicAssessment,
  TopicAnswerKey,
  TeacherSubtopicOverview,
  StudentSubtopicOverview,
  EngageActivityNote,
  RetrofittedStudentLesson,
  RetrofittedStudentLessonTeacherNote,
};

const Glossary = [
  'Glossary',
  'GlossaryTerm',
  'GlossaryTermLocale',

  'Text',
  'Latex',
  'LatexTextAtom',
  'Bold',
  'Italics',

  'Image',
];

export function getComponentWhitelistForDocType(docType: DocType) {
  // Exhaustive switch so Flow can enforce that all docTypes are handled.
  // prettier-ignore
  switch (docType) {
    case 'RetrofittedStudentLesson': return RetrofittedStudentLesson;
    case 'RetrofittedStudentLessonTeacherNote': return RetrofittedStudentLessonTeacherNote;
    case 'Worksheet': return Worksheet;
    case 'AnswerKey': return AnswerKey;
    case 'PrimaryTeacherNote': return PrimaryTeacherNote;
    case 'Playground': return Playground;
    case 'EngageActivity': return EngageActivity;
    case 'ActivityTeacherNote': return ActivityTeacherNote;
    case 'SolidifyLesson': return SolidifyLesson;
    case 'SolidifyTeacherNote': return SolidifyTeacherNote;
    case 'TopicOverview': return TopicOverview;
    case 'TopicAssessment': return TopicAssessment;
    case 'TopicAnswerKey': return TopicAnswerKey;
    case 'TeacherSubtopicOverview': return TeacherSubtopicOverview;
    case 'StudentSubtopicOverview': return StudentSubtopicOverview;
    case 'EngageActivityNote': return EngageActivityNote;
    case 'Investigation': return Investigation;
    case 'InvestigationTeacherNote': return InvestigationTeacherNote;
    case 'Glossary': return Glossary;
    default: {
      (docType: empty)
      throw new Error('Exhaustiveness invariant violated at runtime');
    }
  }
}

// Given a componentName, produces an object that describes the document types
// that the componentName is allowed in, as well those document types it is
// disallowed in. Main intent is for better error messaging and documentation.
export function usageRestrictionsForComponent(componentName: ComponentName) {
  const allowedIn = [];
  const disallowedIn = [];
  // Playground isn't a "proper" document type so we don't want out error messages
  // and documentation hints to muddy the water by mentioning it as a place where
  // you can use components.
  const filteredDocTypes = Object.keys(componentWhitelistsByDocType).filter(
    (n) => n !== 'Playground',
  );
  for (const docType of filteredDocTypes) {
    const components = componentWhitelistsByDocType[docType];
    if (components.includes(componentName)) {
      allowedIn.push(docType);
    } else {
      disallowedIn.push(docType);
    }
  }
  return {
    areRestrictions: allowedIn.length < filteredDocTypes.length,
    allowedIn,
    disallowedIn,
  };
}

// ----------------------------------------------------------------------------
// Editing environment whitelisting
//
// This concept differs to document-oriented whitelisting. We have certain
// editing environments that have emerged which do not operate on entire
// documents (in the case of the engage activity editor), or do not operate
// on a document concept at all (the diagnostic question editor).
// ----------------------------------------------------------------------------
export type EditingEnvironment =
  | 'engageActivity'
  // 🚨 Deprecated (left for easier migration)
  // TODO remove after refactoring jsx-content consumers
  | 'diagnosticQuestion'
  | 'diagnosticQuestionDraft'
  | 'diagnosticQuestionPublication';
export function getComponentWhitelistForEditingEnvironment(
  editingEnvironment: EditingEnvironment,
): Array<string> {
  switch (editingEnvironment) {
    case 'engageActivity':
      return editingEnvironmentEngageActivity;
    // TODO remove
    case 'diagnosticQuestion':
      return editingEnvironmentDiagnosticQuestionDraft;
    case 'diagnosticQuestionDraft':
      return editingEnvironmentDiagnosticQuestionDraft;
    case 'diagnosticQuestionPublication':
      return editingEnvironmentDiagnosticQuestionPublication;
    default:
      (editingEnvironment: empty);
      throw new Error(
        `Invariant violated: editingEnvironment must not be "${editingEnvironment}"`,
      );
  }
}

const editingEnvironmentEngageActivity: Array<string> = EngageActivity.filter(
  (componentName) => {
    const bannedComponents = new Set(['EngageActivity', 'Slides']);
    return bannedComponents.has(componentName) === false;
  },
);

const nonPublishableDiagnosticQuestionComponents = ['DraftImage'];

const editingEnvironmentDiagnosticQuestionDraft: Array<string> = [
  // Basic text stuff
  'Text',
  'Bold',
  'Latex',
  'LatexTextAtom',
  ...ListComponents,

  // Figures
  'Image',
  'TabularData',
  'BoxAndWhiskerPlot',
  ...CartesianPlaneComponents,
  ...PseudoCodeBlockComponents,
  ...NumberLineComponents,
  ...StemAndLeafPlotComponents,
  ...AlgebraicWorkingComponents,

  // Draft-specific
  ...nonPublishableDiagnosticQuestionComponents,
];

const editingEnvironmentDiagnosticQuestionPublication: Array<string> =
  editingEnvironmentDiagnosticQuestionDraft.filter((componentName) => {
    const nonPublishable = new Set(nonPublishableDiagnosticQuestionComponents);
    return nonPublishable.has(componentName) === false;
  });
